import React, { FormEvent, useState } from 'react';
import {
    Button,
    Dropdown,
    Group,
    InformationType,
    TextInput,
    useTheme,
    Text,
    TextType,
    Grid,
    Heading,
    Level,
    ProgressIndicator,
    ProgressType,
    Screen,
    ThemeProvider,
    themes,
    Logo,
    Icon,
    TextInputMode,
    CheckCircleIcon,
} from '@defa/defa-component-library';
import { isAndroid, isIOS } from '@utils/detect-device';
import { CountryType, getCountryCodeValueList } from '@utils/country-list';
import { updatePhoneNumberWithCountry, validateCombinedPhoneNumber } from '@utils/phone-number';
import { AndroidDownload, IconContainer, IOSDownload } from './anonymous-invite-home.styles';
import i18n from '@i18n';
import { AnonymousSetupResponseStatus } from '@generated/admin-prime/types.gen';
import {
    getChargeSystemSetupInfoQueryOptions,
    useAcceptHomeChargeSystemSetupMutation,
} from '@generated/admin-prime/@tanstack/react-query.gen';
import { useQuery } from '@tanstack/react-query';
import { useSupportedCountries } from '../../shared/countries/countries.hooks';
import { useSearchParams } from 'react-router-dom';

function AppIcons() {
    const unknownPlatform = !isIOS() && !isAndroid();
    return (
        <IconContainer>
            {(isIOS() || unknownPlatform) && (
                <IOSDownload
                    href="https://apps.apple.com/se/app/defa-power/id6443797468"
                    target="_blank"
                    rel="noopener noreferrer"
                />
            )}
            {(isAndroid() || unknownPlatform) && (
                <AndroidDownload
                    href="https://play.google.com/store/apps/details?id=com.defa.powerapp&pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                />
            )}
        </IconContainer>
    );
}

export const AnonymousInviteHome: React.FunctionComponent = () => {
    const theme = useTheme();
    const [query] = useSearchParams();
    const token = query.get('token');

    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [selectedCountryCode, setSelectedCountryCode] = useState<string | undefined>();

    const {
        data: chargeSystemSetupInfo,
        error: setupInfoError,
        isPending,
    } = useQuery({
        ...getChargeSystemSetupInfoQueryOptions({ path: { token: token ?? '' } }),
        enabled: !!token,
    });

    const { countryCodes } = useSupportedCountries();
    const countriesList: CountryType[] = getCountryCodeValueList(countryCodes);
    const country = countriesList.find((c) => c.countryCode === selectedCountryCode);

    const {
        mutate: acceptInvite,
        data: acceptResponse,
        isPending: loadingSave,
        isError: acceptThrew,
    } = useAcceptHomeChargeSystemSetupMutation();

    const isDone = acceptResponse?.status === AnonymousSetupResponseStatus.SUCCESS;
    const submitFailed = acceptThrew || (acceptResponse != null && !isDone);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        acceptInvite({
            body: {
                phoneNumber: updatePhoneNumberWithCountry(phoneNumber, country?.callingCode ?? ''),
                countryCode: country?.countryCode ?? '',
                token: token ?? '',
            },
        });
    };

    const isPhoneNumberOk = validateCombinedPhoneNumber(country?.callingCode ?? '', phoneNumber);

    const isAlreadyAccepted =
        chargeSystemSetupInfo?.status === AnonymousSetupResponseStatus.ALREADY_ACCEPTED_INVITE;
    const hasError = !token || !!setupInfoError || submitFailed;
    const showForm = !isDone && !hasError && !!chargeSystemSetupInfo && !isAlreadyAccepted;
    const isLoading = !!token && isPending;

    return (
        <ThemeProvider theme={themes[1]}>
            <Screen>
                <Group maxWidth="480px" width="100%" form onSubmit={submit}>
                    <Logo variant="defa" size={64} />

                    {isLoading && (
                        <IconContainer>
                            <ProgressIndicator progress={0} type={ProgressType.DONUT_LOADING} />
                        </IconContainer>
                    )}

                    {isAlreadyAccepted && (
                        <Group>
                            <Heading level={Level.h1}>
                                {i18n.t('AnonymousInviteHome.AlreadyAcceptedHeader')}
                            </Heading>
                            <Text type={TextType.body}>
                                {i18n.t('AnonymousInviteHome.AlreadyAcceptedBody')}
                            </Text>
                            <AppIcons />
                        </Group>
                    )}

                    {hasError && (
                        <Group>
                            <Heading level={Level.h1}>
                                {i18n.t('AnonymousInviteHome.ErrorHeader')}
                            </Heading>
                            <Text type={TextType.body}>
                                {i18n.t('AnonymousInviteHome.ErrorBody')}
                            </Text>
                            <Button
                                text={i18n.t('Back')}
                                onClick={() =>
                                    window.location.replace(
                                        'https://www.defa.com/support/defa-power/'
                                    )
                                }
                            />
                        </Group>
                    )}

                    {isDone && (
                        <>
                            <IconContainer>
                                <Icon icon={CheckCircleIcon} color="#66BB6A" size={48} />
                            </IconContainer>
                            <Group>
                                <Heading level={Level.h1}>
                                    {i18n.t('AnonymousInviteHome.SuccessHeader')}
                                </Heading>
                                <Text>{i18n.t('AnonymousInviteHome.SuccessBody')}</Text>
                            </Group>
                            <AppIcons />
                        </>
                    )}

                    {showForm && (
                        <>
                            <Group>
                                <Heading level={Level.h1}>
                                    {i18n.t('AnonymousInviteHome.Header')}
                                </Heading>
                                <Text type={TextType.body}>
                                    {i18n.t('AnonymousInviteHome.Body')}
                                </Text>
                            </Group>
                            <Group>
                                <Grid gap={theme.spacingRaw(2)} columns="1fr 2fr" rows="auto">
                                    <Dropdown<string>
                                        name="country"
                                        label={i18n.t('AnonymousInviteHome.CountryLabel')}
                                        placeholder={i18n.t(
                                            'OrganizationSettings.CountryPlaceholder'
                                        )}
                                        onChange={(selectedOption) => {
                                            const normalized = selectedOption.toUpperCase();
                                            setSelectedCountryCode(normalized);
                                        }}
                                        value={selectedCountryCode}
                                        disabled={loadingSave}
                                        items={countriesList.map((c) => c.countryCode)}
                                        keyExtractor={(option) => option}
                                        labelExtractor={(option) => {
                                            const matched = countriesList.find(
                                                (c) => c.countryCode === option
                                            );
                                            return matched
                                                ? `${matched.flag} ${matched.callingCode} ${matched.countryCode}`
                                                : option;
                                        }}
                                    />
                                    <TextInput
                                        label={i18n.t('AnonymousInviteHome.PhoneNumberLabel')}
                                        name="phone"
                                        value={phoneNumber}
                                        onChange={setPhoneNumber}
                                        mode={TextInputMode.PHONE}
                                        autocomplete="username"
                                        disabled={loadingSave}
                                        informationType={InformationType.ERROR}
                                        message={
                                            isPhoneNumberOk
                                                ? undefined
                                                : i18n.t('PhoneNumberInvalid')
                                        }
                                    />
                                </Grid>
                                <Group>
                                    <Button
                                        text={i18n.t('AnonymousInviteHome.SubmitButton')}
                                        disabled={!isPhoneNumberOk || loadingSave}
                                        type="submit"
                                        name="login-button"
                                        loading={loadingSave}
                                    />
                                </Group>
                            </Group>
                        </>
                    )}
                </Group>
            </Screen>
        </ThemeProvider>
    );
};
